import React from 'react'
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from '@react-pdf/renderer'

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 12,
    fontFamily: 'Helvetica',
    backgroundColor: '#f7f7f7',
  },
  headerPresupuesto: {
    backgroundColor: '#333',
    color: 'white',
    padding: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    padding: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'black',
    fontSize: 10,
    fontWeight: 'bold',
  },
  projectTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 12,
    paddingHorizontal: 3,
  },
  projectTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginLeft: 185,
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 10,
  },
  section: {
    marginBottom: 15,
    paddingHorizontal: 10,
  },
  sectionTitleContainer: {
    backgroundColor: '#c2c2c2',
    padding: 8,
    borderRadius: 4,
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: 'white',
    textTransform: 'uppercase',
  },
  table: {
    display: 'table',
    width: '100%',
    marginTop: 8,
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: '#eaeaea',
    fontWeight: 'bold',
    fontSize: 10,
    flexDirection: 'row',
    paddingVertical: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  tableCell: {
    flex: 1,
    padding: 5,
    textAlign: 'left',
    fontSize: 10,
  },
  totalContainer: {
    backgroundColor: '#333',
    color: 'white',
    flexDirection: 'column',
    padding: 10,
    borderRadius: 6,
    marginTop: 10,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  totalLabel: {
    fontWeight: 'bold',
    fontSize: 12,
    color: 'white',
  },
  totalValue: {
    fontWeight: 'bold',
    fontSize: 12,
    color: 'white',
  },
  pie: {
    flex: 1,
    textAlign: 'center',
    color: 'black',
    fontSize: 12,
  },
})

const ResumenPDF = ({resumen}) => {

  const {
    compraSolar,
    construccion,
    costesGenerales,
    otrosHonorarios,
    totalImporte,
    numeroDivision,
    currentProject,
    fabricacionIndustrial_v2,
  } = resumen


  const formatNumber = num =>
    Number(num || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })

  const calculateEurM2 = value =>
    numeroDivision > 0 ? (value / numeroDivision).toFixed(2) : '0.00'

  const calculatePercentage = (value, total) =>
    total > 0 ? ((value / total) * 100).toFixed(2) : '0.00'

  const renderTable = (title, data, isConstruccion, extraValue) => {
    const filteredData = data.filter(
      item => item.name.toLowerCase() !== 'fabricación industrial'
    )

    const sectionSum =
      filteredData.reduce((sum, item) => sum + Number(item.value || 0), 0) +
      (isConstruccion && extraValue ? Number(extraValue) : 0)

    const eurM2Total = calculateEurM2(sectionSum)

    return (
      <View style={styles.section} wrap={false}>
        <View style={styles.sectionTitleContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        <Text>
          {formatNumber(sectionSum)} € | {eurM2Total} €/m²
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            {['Concepto', 'Importe (€)', '€/m²', '%'].map((h, i) => (
              <Text
                key={i}
                style={{
                  ...styles.tableCell,
                  fontWeight: 'bold',
                  textAlign: i > 0 ? 'right' : 'left',
                }}
              >
                {h}
              </Text>
            ))}
          </View>

          {isConstruccion && extraValue && (
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Fabricación Industrial</Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {formatNumber(extraValue)}
              </Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {calculateEurM2(extraValue)}
              </Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {calculatePercentage(extraValue, sectionSum)} %
              </Text>
            </View>
          )}

          {filteredData.map((item, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.tableCell}>{item.name}</Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {formatNumber(item.value)}
              </Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {calculateEurM2(item.value)}
              </Text>
              <Text style={{ ...styles.tableCell, textAlign: 'right' }}>
                {calculatePercentage(item.value, sectionSum)} %
              </Text>
            </View>
          ))}
        </View>
      </View>
    )
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.projectTitleContainer} fixed>
          <Image src="/logo_jit.jpg" style={styles.logo} />
          <Text style={styles.projectTitle}>{currentProject.name}</Text>
        </View>

        <View style={styles.headerPresupuesto}>
          <Text>Resumen Presupuesto</Text>
          <Text>{formatNumber(totalImporte)} €</Text>
        </View>

        {renderTable('Compra Solar', compraSolar, false)}
        {renderTable(
          'Construcción',
          construccion,
          true,
          fabricacionIndustrial_v2
        )}
        {renderTable('Costes Generales', costesGenerales, false)}
        {renderTable('Otros Honorarios', otrosHonorarios, false)}

        <View style={styles.totalContainer}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Importe Final:</Text>
            <Text style={styles.totalValue}>
              {formatNumber(totalImporte)} €
            </Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>€/m² Total:</Text>
            <Text style={styles.totalValue}>
              {calculateEurM2(totalImporte)} €/m²
            </Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.pie}>{currentProject.name}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  )
}

export default ResumenPDF
